"use client";

// Homepage v2 navigation, in two places: inside the hero card (Nav) and a fixed bar that takes over once the card has
// left the screen (FixedNav). Only one of them is focusable at a time (the other is inert).
// Each list has a single pill: one element moved with a transform, its width scaled on the way (FLIP). It follows the
// current section and the hovered item, and comes back to the current section when the pointer leaves. An item is
// current only while its own block is on screen: none at the top of the page, none below Skills. With nothing current
// (and nothing hovered) the pill fades out; it fades back in on its item.
// FixedNav also feeds the hero its scroll progress (CSS variables, one requestAnimationFrame per scroll) and pauses the
// hero's loops off screen: the hero stays a server component, and components/motion has no scroll helper yet.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { stagger } from "@/components/motion/stagger";
import "@/components/ds/button.css"; // .ds-edge: the glass stroke of the pill
import { NAV_ITEMS } from "./sections";
import "./nav.css";

type NavId = (typeof NAV_ITEMS)[number]["id"];

// A section becomes active once its top edge crosses this line (share of the viewport height).
const ACTIVE_LINE = 0.4;
// The hero card stretches to the edges over this much scrolling.
const STRETCH_DISTANCE = 300;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
// A CSS time read back from a custom property: the stylesheet is minified, so 320ms comes back as .32s.
const toMs = (time: string, fallback: number) => {
  const value = Number.parseFloat(time);
  if (Number.isNaN(value)) return fallback;
  return time.trim().endsWith("ms") ? value : value * 1000;
};
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function useActiveSection() {
  // Top of the page: no block of the nav has reached the line yet, so nothing is current (the hero is on screen).
  const [active, setActive] = useState<NavId | null>(null);

  useEffect(() => {
    const sections = NAV_ITEMS.map(({ id }) => document.getElementById(id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (sections.length === 0 || !("IntersectionObserver" in window)) return;

    // Active item = the last section whose top is above the line, which keeps Features under OS; nothing before Why
    // Claude reaches the line, and nothing once the last one (Skills) has left it (manifesto, customer, FAQ, form,
    // footer). The observer only says when to recompute: it watches the band above the line on every block of the
    // page, so both a slow scroll and a jump (anchor, Home key) fire it.
    const update = () => {
      const line = window.innerHeight * ACTIVE_LINE;
      let current: NavId | null = null;
      for (const el of sections) {
        if (el.getBoundingClientRect().top <= line) current = el.id as NavId;
      }
      const last = sections[sections.length - 1];
      if (last && current === last.id && last.getBoundingClientRect().bottom <= line) current = null;
      setActive(current);
    };

    const io = new IntersectionObserver(update, {
      rootMargin: `0px 0px -${(1 - ACTIVE_LINE) * 100}% 0px`,
    });
    new Set([...sections, ...document.querySelectorAll("main > *, body > footer")]).forEach((el) =>
      io.observe(el),
    );
    update();
    return () => io.disconnect();
  }, []);

  return active;
}

/** True once the hero card has scrolled entirely above the viewport. */
function useHeroGone(anchor: RefObject<HTMLElement | null>) {
  const [gone, setGone] = useState(false);

  useEffect(() => {
    const card = anchor.current?.closest(".v2-hero")?.querySelector(".v2-hero__card");
    if (!card || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry) setGone(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
    });
    io.observe(card);
    return () => io.disconnect();
  }, [anchor]);

  return gone;
}

/** Scroll progress of the hero as CSS variables (read by hero.css), and data-idle while it is off screen or the tab hidden. */
function useHeroScroll(anchor: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const hero = anchor.current?.closest<HTMLElement>(".v2-hero");
    if (!hero) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      const top = hero.getBoundingClientRect().top;
      const height = hero.offsetHeight || 1;
      hero.style.setProperty("--v2-hero-stretch", clamp01(-top / STRETCH_DISTANCE).toFixed(4));
      hero.style.setProperty("--v2-hero-exit", clamp01(-top / height).toFixed(4));
      hero.toggleAttribute("data-idle", top + height <= 0 || document.hidden);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("visibilitychange", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [anchor]);
}

type Geometry = { x: number; w: number };

/**
 * Puts the pill on link `index`. The pill lives in the first item (inset: 0), so on the first link it has no inline
 * style at all and is exactly the server-rendered one. Elsewhere: width + translateX. With `animate`, it flies from
 * where it is drawn now (a flight in progress included) with a scaleX to its new width (transform-origin: left).
 */
function movePill(
  list: HTMLElement,
  pill: HTMLElement,
  index: number,
  animate: boolean,
  flight: RefObject<Animation | null>,
  geometry: RefObject<Geometry | null>,
) {
  const home = pill.parentElement;
  const link = list.querySelectorAll<HTMLElement>(".v2-nav__link")[index];
  if (!home || !link) return;

  const origin = home.getBoundingClientRect();
  const transform = getComputedStyle(pill).transform;
  const drawn = transform && transform !== "none" ? new DOMMatrixReadOnly(transform) : new DOMMatrixReadOnly();
  const from = { x: drawn.e, w: (geometry.current?.w ?? origin.width) * drawn.a };

  flight.current?.cancel();
  flight.current = null;

  const rect = link.getBoundingClientRect();
  const to = index === 0 ? { x: 0, w: origin.width } : { x: rect.left - origin.left, w: rect.width };
  if (index === 0) {
    pill.style.removeProperty("width");
    pill.style.removeProperty("transform");
  } else {
    pill.style.width = `${to.w}px`;
    pill.style.transform = `translateX(${to.x}px)`;
  }
  geometry.current = to;

  if (!animate || to.w === 0 || (Math.abs(from.x - to.x) < 0.5 && Math.abs(from.w - to.w) < 0.5)) return;
  const style = getComputedStyle(pill);
  flight.current = pill.animate(
    [{ transform: `translateX(${from.x}px) scaleX(${from.w / to.w})` }, { transform: `translateX(${to.x}px)` }],
    {
      duration: toMs(style.getPropertyValue("--v2-dur-base"), 320),
      easing: style.getPropertyValue("--v2-ease-out").trim() || "ease-out",
    },
  );
}

/**
 * Fades the pill in or out where it stands, over --v2-dur-base: opacity and a slight scale. React has already set the
 * resting state (data-off when hidden); the animation runs from the other state to it, or from the values drawn now if
 * a fade is reversed mid-way, then is dropped so the stylesheet holds the end state.
 */
function fadePill(pill: HTMLElement, show: boolean, fade: RefObject<Animation | null>) {
  const style = getComputedStyle(pill);
  const reversing = fade.current?.playState === "running";
  // Shown, the pill follows the hero card's fade (--v2-hero-fade, in the card nav).
  const on = { opacity: style.getPropertyValue("--v2-hero-fade").trim() || "1", scale: "1" };
  const off = { opacity: "0", scale: "0.9" };
  const drawn = { opacity: style.opacity, scale: style.scale === "none" ? "1" : style.scale };
  fade.current?.cancel();
  const animation = pill.animate([reversing ? drawn : show ? off : on, show ? on : off], {
    duration: toMs(style.getPropertyValue("--v2-dur-base"), 320),
    easing: style.getPropertyValue("--v2-ease-out").trim() || "ease-out",
  });
  fade.current = animation;
  animation.onfinish = () => {
    animation.cancel();
    if (fade.current === animation) fade.current = null;
  };
}

function NavList({ active, current }: { active: NavId | null; current: boolean }) {
  const listRef = useRef<HTMLUListElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const flight = useRef<Animation | null>(null);
  const fade = useRef<Animation | null>(null);
  const geometry = useRef<Geometry | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  const activeIndex = NAV_ITEMS.findIndex((item) => item.id === active);
  // Nothing lit: no current section and no hovered item. The server renders that state (pill off).
  const lit = hovered ?? (activeIndex >= 0 ? activeIndex : null);
  const litRef = useRef(lit);
  const shownRef = useRef<number | null>(null);

  // Layout effect: the pill is placed (and its fade started) before the browser paints the new data-off state.
  useLayoutEffect(() => {
    const list = listRef.current;
    const pill = pillRef.current;
    if (!list || !pill) return;
    const was = shownRef.current;
    shownRef.current = lit;
    litRef.current = lit;
    const motion = !reducedMotion();
    if (lit === null) {
      if (was !== null && motion) fadePill(pill, false, fade);
      return;
    }
    // From one item to another it flies; coming back from hidden it is put in place, then fades in.
    movePill(list, pill, lit, was !== null && motion, flight, geometry);
    if (was === null && motion) fadePill(pill, true, fade);
  }, [lit]);

  // Fonts arriving, the window resized, the mobile layout: put the pill back on its item without animating.
  useEffect(() => {
    const list = listRef.current;
    const pill = pillRef.current;
    if (!list || !pill || !("ResizeObserver" in window)) return;
    const ro = new ResizeObserver(() => {
      if (litRef.current !== null) movePill(list, pill, litRef.current, false, flight, geometry);
    });
    ro.observe(list);
    return () => ro.disconnect();
  }, []);

  return (
    <ul
      ref={listRef}
      className="v2-nav__list"
      onPointerLeave={(event) => {
        if (event.pointerType !== "touch") setHovered(null);
      }}
    >
      {NAV_ITEMS.map(({ id, label }, index) => (
        <li key={id} className="v2-nav__item" style={stagger(index)}>
          {index === 0 && (
            <span ref={pillRef} className="v2-nav__pill ds-edge" aria-hidden="true" data-off={lit === null ? "" : undefined} />
          )}
          <a
            className={`v2-nav__link${index === 0 ? " v2-nav__link--lead" : ""}${active === id ? " is-active" : ""}${lit === index ? " is-lit" : ""}`}
            href={`#${id}`}
            aria-current={current && active === id ? "true" : undefined}
            onPointerEnter={(event) => {
              if (event.pointerType !== "touch") setHovered(index);
            }}
          >
            <span className="v2-nav__label">{label}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/**
 * Link to the top with the North logo inside. Hovering it with a mouse or a pen replays the logo's own animation
 * (hero.css, v2-logo-*): its CSS animations are sent back to the start of their sequence. Not while they still play,
 * not on keyboard focus, and never with reduced motion (there are no animations then).
 */
export function LogoLink({ className, children }: { className: string; children: ReactNode }) {
  return (
    <a
      className={className}
      href="#top"
      aria-label="North home"
      onPointerEnter={(event) => {
        if (event.pointerType === "touch") return;
        const animations = event.currentTarget
          .getAnimations({ subtree: true })
          .filter((animation): animation is CSSAnimation => animation instanceof CSSAnimation && animation.animationName.startsWith("v2-logo-"));
        if (animations.length === 0 || animations.some((animation) => animation.playState === "running")) return;
        // The sequence starts at the smallest delay (the first shape of the mark): skip to it, keep the stagger.
        const start = Math.min(...animations.map((animation) => Number(animation.effect?.getTiming().delay ?? 0)));
        for (const animation of animations) {
          animation.currentTime = start;
          animation.play();
        }
      }}
    >
      {children}
    </a>
  );
}

/** The nav inside the hero card. */
export default function Nav() {
  const ref = useRef<HTMLElement>(null);
  const active = useActiveSection();
  const gone = useHeroGone(ref);

  return (
    <nav ref={ref} className="v2-nav v2-nav--card" aria-label="Primary" inert={gone}>
      <NavList active={active} current={!gone} />
    </nav>
  );
}

/** The fixed bar shown once the hero card is off screen. `logo` is the server-rendered North logo. */
export function FixedNav({ logo }: { logo: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const active = useActiveSection();
  const gone = useHeroGone(ref);
  useHeroScroll(ref);

  return (
    <div ref={ref} className="v2-fixednav" data-shown={gone ? "" : undefined} inert={!gone}>
      <div className="v2-fixednav__inner">
        <LogoLink className="v2-fixednav__logo">{logo}</LogoLink>
        <nav className="v2-nav v2-nav--fixed" aria-label="Primary">
          {/* While the bar is hidden nothing is current in it: the pill fades in as the bar arrives on Why Claude. */}
          <NavList active={gone ? active : null} current={gone} />
        </nav>
      </div>
    </div>
  );
}
